
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Coins, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface CommonExpensesCalculatorProps {
  commonExpenseResult: string;
  setCommonExpenseResult?: (value: string) => void;
  selectedHouse?: string;
  selectedMonth?: string;
}

export const CommonExpensesCalculator = ({
  commonExpenseResult,
  setCommonExpenseResult = () => {},
  selectedHouse = "",
  selectedMonth = ""
}: CommonExpensesCalculatorProps) => {
  const [commonExpenseRate, setCommonExpenseRate] = useState<string>("");
  const [isCreatingExpense, setIsCreatingExpense] = useState(false);
  const { toast } = useToast();
  
  // State to store the parsed expense value
  const [parsedExpenseValue, setParsedExpenseValue] = useState<number | null>(null);

  // Calculate result when inputs change
  useEffect(() => {
    if (!commonExpenseRate || commonExpenseRate === '') {
      setCommonExpenseResult("Ingrese el valor");
      setParsedExpenseValue(null);
      return;
    }

    const expenseValue = parseFloat(commonExpenseRate);
    
    if (isNaN(expenseValue)) {
      setCommonExpenseResult("Error: El valor no es válido");
      setParsedExpenseValue(null);
      return;
    }
    
    // Divide the expense value by 7
    const calculatedValue = expenseValue / 7;
    setParsedExpenseValue(calculatedValue);
    
    const formattedExpense = new Intl.NumberFormat('es-CL', { 
      style: 'currency', 
      currency: 'CLP'
    }).format(calculatedValue);
    
    setCommonExpenseResult(`Costo por persona: ${formattedExpense}`);
  }, [commonExpenseRate, setCommonExpenseResult]);

  // Create an expense from the calculation
  const handleCreateExpense = async () => {
    if (!selectedHouse || !selectedMonth || !parsedExpenseValue || parsedExpenseValue <= 0) {
      toast({
        title: "Error",
        description: "Faltan datos para crear el gasto. Asegúrese de que haya seleccionado una casa y el cálculo sea válido.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreatingExpense(true);
      
      // Get the last day of the selected month for expense_date
      const [year, month] = selectedMonth.split("-").map(Number);
      const lastDay = new Date(year, month, 0).getDate();
      const expenseDate = `${selectedMonth}-${lastDay.toString().padStart(2, '0')}`;

      // Fetch common expense category ID
      const { data: categories, error: categoryError } = await supabase
        .from("expense_categories")
        .select("id")
        .eq("name", "Gasto común")
        .limit(1);

      if (categoryError || !categories || categories.length === 0) {
        throw new Error("No se pudo encontrar la categoría Gasto común");
      }

      const categoryId = categories[0].id;
      
      // Insert the expense
      const { error } = await supabase.from("expenses").insert({
        entity_id: selectedHouse,
        entity_type: 'house',
        amount: Math.round(parsedExpenseValue),
        category: categoryId,
        description: "Gasto común mensual",
        expense_date: expenseDate,
      });

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Gasto común creado correctamente",
      });
    } catch (error) {
      console.error("Error creating expense:", error);
      toast({
        title: "Error",
        description: "No se pudo crear el gasto común",
        variant: "destructive",
      });
    } finally {
      setIsCreatingExpense(false);
    }
  };

  // Check if the calculation result is valid for creating an expense
  const hasValidResult = 
    commonExpenseResult && 
    commonExpenseResult.includes("Costo por persona:") && 
    parsedExpenseValue !== null && 
    parsedExpenseValue > 0;

  return (
    <Card className="shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg font-medium">
          <Coins className="h-5 w-5 text-blue-500" />
          Gasto Común
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="common-expense-rate">Valor comunitarios del mes</Label>
            <Input
              id="common-expense-rate"
              type="number"
              placeholder="Ingrese valor de gastos comunitarios"
              className="bg-white"
              value={commonExpenseRate}
              onChange={(e) => setCommonExpenseRate(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="common-expense-result">Resultado</Label>
            <div id="common-expense-result" className="p-3 rounded-md border bg-gray-50 text-gray-700 whitespace-pre-line">
              {commonExpenseResult || "Resultado del cálculo"}
            </div>
          </div>

          <Button 
            onClick={handleCreateExpense} 
            disabled={!hasValidResult || !selectedHouse || isCreatingExpense}
            className="w-full"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Crear gasto común
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
